import React, {useCallback, useState} from 'react';
import {useRoomPlannerLeftMenu} from 'components/customer/RoomPlanner/helper/useRoomPlannerLeftMenu';
import styled from 'styled-components';
import {
    getIsEditingRoomDimension,
    getisRoomPlannerEditing,
} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {useAppSelector} from 'store/customer';
import {SurveyButton} from 'shared/components/Header/RoomPlanner/SurveyButton';
import {TutorialButton} from 'shared/components/Header/RoomPlanner/TutorialButton';
import FloatingMenu from 'components/customer/RoomPlanner/components/FloatingMenu/FloatingMenu';
import PlaceholderFloatingMenu from 'components/customer/RoomPlanner/components/FloatingMenu/PlaceholderFloatingMenu';
import {useRoomPlannerContext} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import {ProductDropdown} from 'components/customer/RoomPlanner/components/FloatingMenu/ProductDropdownFloatingComponent';
import {PlaceholderDropdown} from 'components/customer/RoomPlanner/components/FloatingMenu/PlaceholderDropdown';
import {useResizeObserver} from 'shared/helpers/useResizeObserver';
import {
    mobileLandscape,
    mobilePortrait,
    tablet,
    tabletLandscape,
} from 'shared/Breakpoints';
import {selectLeftSidebar, SidebarStatus} from 'store/customer/UISlice';
import PositionEditor from 'components/customer/RoomPlanner/components/FloatingMenu/PositionEditor';
import {isMobile} from 'react-device-detect';
import {
    useIsMobileLandscape,
    useIsPortraitDevices,
} from 'shared/helpers/DeviceSize';
import ShareButton from 'shared/components/Header/ShareButton';

const containerID = 'mobile-product-controls';

export const Mobile = () => {
    const isPortraitDevices = useIsPortraitDevices();
    const isMobileLandscape = useIsMobileLandscape();
    const {isRoomPlannerPage} = useRoomPlannerLeftMenu();
    const {selectedCabinetGroup, selectedProduct, activeDirection, editorMode} =
        useRoomPlannerContext();
    const isPlaceholder = Boolean(
        selectedCabinetGroup?.userData?.isPlaceholder
    );
    const isRoomPlannerEditing = useAppSelector(getisRoomPlannerEditing);
    const leftSidebar = useAppSelector(selectLeftSidebar);
    const isEditingDimensions = useAppSelector(getIsEditingRoomDimension);

    const [showButtonText, setShowButtonText] = useState(true);
    const [allowVerticalScroll, setAllowVerticalScroll] = useState(false);

    const getTotalChildrenHeight = useCallback(() => {
        const container = document.querySelector(`#${containerID}`);

        let height = 0;
        Array.from(container.children).forEach((child) => {
            const {height: childHeight} = child.getBoundingClientRect();

            height += childHeight;
        });

        return height;
    }, []);

    const [displayInfoIcons, setDisplayInfoIcons] = useState<boolean>();
    const container = useResizeObserver((entries: ResizeObserverEntry[]) => {
        if (entries.length > 0) {
            const container = entries[0];
            if (!container) return;

            const {width, height: containerHeight} = container.contentRect;

            if (width >= 414) {
                setDisplayInfoIcons(true);
            }

            let height = getTotalChildrenHeight();

            if (height > containerHeight) {
                setShowButtonText(false);

                setTimeout(() => {
                    height = getTotalChildrenHeight();

                    if (height > containerHeight + 10) {
                        setAllowVerticalScroll(true);
                    }
                }, 50);
            }
        }
    });

    if (isPortraitDevices && isEditingDimensions) {
        return null;
    }

    if (isRoomPlannerPage) {
        return (
            <Container
                $sidebarOpen={leftSidebar == SidebarStatus.OPEN}
                $productSelected={!!selectedProduct && !isRoomPlannerEditing}>
                <MiscButtons>
                    <ButtonSection>
                        <ShareButton />
                        <TutorialButton />
                        <SurveyButton />
                    </ButtonSection>
                </MiscButtons>

                {selectedCabinetGroup && !isRoomPlannerEditing && isMobile ? (
                    <Controls
                        id={containerID}
                        ref={container}
                        $allowScroll={allowVerticalScroll}>
                        {(!selectedCabinetGroup ||
                            !activeDirection ||
                            !editorMode) &&
                        (isPortraitDevices || isMobileLandscape) ? (
                            <>
                                <Buttons $showButton={showButtonText}>
                                    {isPlaceholder ? (
                                        <PlaceholderFloatingMenu />
                                    ) : (
                                        <FloatingMenu />
                                    )}
                                </Buttons>
                                {isPlaceholder ? (
                                    <PlaceholderDropdown />
                                ) : (
                                    <ProductDropdown
                                        infoDisplayOverride={displayInfoIcons}
                                    />
                                )}
                            </>
                        ) : null}

                        <PositionEditor />
                    </Controls>
                ) : null}
            </Container>
        );
    }
};

const ButtonSection = styled.div`
    display: flex;
    flex-direction: row;
    flex-shrink: 0;
    gap: 10px;

    & > * {
        pointer-events: auto;
    }

    > div {
        margin: 0;
    }

    button {
        margin: 0;
    }
`;

const MiscButtons = styled.div`
    display: flex;
    justify-content: space-between;
    text-align: center;
    padding: 10px 10px;

    ${mobileLandscape`
        justify-content: flex-start;
        gap: 10px;
        width: fit-content;
    `}

    ${mobilePortrait`
        position: relative;
        height: 0;
        padding: 0;

        > div {
            position: absolute;
            right: 10px;
            bottom: 10px;
        }
    `}
`;

const Controls = styled.div<{$allowScroll: boolean}>`
    background: white;
    box-shadow: 0 0 9px 1px #d1d1d1;

    ${({$allowScroll}) => {
        if ($allowScroll) {
            return `
                overflow: auto;
            `;
        }
    }}

    ${mobileLandscape`
        width: 50%;
        position: fixed;
        top: 70px;
        right: 0;
        bottom: 0;
        box-shadow: -1px -1px 8px 0px #a1a1a1 inset;
        left: initial;
    `}
`;

const Buttons = styled.div<{$showButton: boolean}>`
    display: flex;
    justify-content: space-evenly;
    padding: 10px 0;

    > button {
        width: 45px;
        height: initial;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;

        > img {
            max-width: 33px;
            width: 33px;
        }

        > label {
            display: ${({$showButton}) => ($showButton ? 'block' : 'none')};
            font-size: 0.65em;
            font-weight: bold;
            color: ${({theme}) => theme.colors.primary.main};
            margin-top: 5px;
        }
    }

    ${mobileLandscape`
        > button {
            width: 40px;
        }
    `}
`;

interface ContainerProps {
    $productSelected?: boolean;
    $sidebarOpen?: boolean;
}

const Container = styled.div<ContainerProps>`
    position: fixed;
    width: 100%;
    right: 0;
    top: initial;
    bottom: 100px;
    display: flex;
    flex-direction: column;
    z-index: 100;

    ${mobileLandscape<ContainerProps>`
        left: ${({$sidebarOpen}) => ($sidebarOpen ? '205px' : '50px')};
        bottom: 0px !important;
        width: fit-content;
    `}

    ${tabletLandscape<ContainerProps>`
        left: ${({$sidebarOpen}) => ($sidebarOpen ? '235px' : '80px')};
        bottom: 0px !important;
        width: initial;
    `}

    ${tablet`
        bottom: 80px;
    `}

    ${({$productSelected}) => {
        if ($productSelected) {
            return `
                bottom: 0 !important;
            `;
        }
    }}
`;
